/**
 * The platform's CSV of a run's lists, read back into rows (`GET /launch/initial/runs/{run_id}/export?kind=`, platform
 * ADR 6194): articles, hold-back scenarios, lines and cells, each in ONE streamed request instead of a walk of JSON pages.
 *
 * The file is `src/export`'s still — the headers, the names beside the codes, both CSV dialects and the «Columns» sheet
 * are built there; what the platform contributes is the ROWS. The mapping is **by column name**: every field is named once
 * below, so a field the file gains is read when this module names it, a column an older platform does not write reads as
 * absent (`null`), and a required column that is missing is a refusal ({@link RunFileError}), never a row with a guessed value.
 * An empty cell is `null`, never nought or an empty string; a decimal stays a string, digit for digit; a text cell the platform
 * neutralised against spreadsheet formulas is restored (the export neutralises it again on the way out, once). Fewer rows than the
 * platform stated (`X-Bata-Row-Count`) is an `ApiIncompleteFileError`: a short list is never written as the whole one.
 *
 * The file is read AS IT ARRIVES, in pieces (`callOperationCsvStream`, docs/adr/9073): it is never one string, nor one array of
 * unparsed rows — a run's cells file ran a tab out of memory at 504,246 rows (2026-10-23) and this one has the same shape. Each
 * row is typed the moment it completes. The CSV reader is the shared `CsvRowReader` of `@/api`
 * (`src/api/csv-rows.ts`).
 */

import { ApiIncompleteFileError, callOperationCsvStream, CsvRowReader, CSV_FORMULA_GUARD, parseCsvText, type CsvProgress, type CsvStream } from '@/api';
import { exportRun, type RunArticle, type RunCell, type RunScenario } from '@/api/generated';

/** A required column is missing from the file, or a cell is not the type its column holds. */
export class RunFileError extends Error {
  constructor(public readonly detail: string) {
    super(`The platform's file of the run's list cannot be read: ${detail}.`);
    this.name = 'RunFileError';
  }
}

/** What the shared CSV reader raises for a file that ends inside a quoted cell. */
const rowReaderError = (message: string): Error => new RunFileError(message);

/** A line as the platform's decision file writes it: no proposal id; the decider by identifier, and by name where the file carries it. */
export interface LineRow {
  readonly article_key: string;
  readonly store_id: string;
  readonly size_key: string;
  readonly size_code: string | null;
  readonly units: number;
  readonly ship_week: string;
  readonly reason_code: string;
  readonly expected_impact: string;
  readonly currency_code: string;
  readonly share: string | null;
  readonly position_units: number | null;
  readonly core_units: number | null;
  readonly quantile_units: number | null;
  readonly need_units: number | null;
  readonly disposition: string | null;
  readonly disposition_reason_code: string | null;
  readonly adjusted_units: number | null;
  readonly disposition_actor: string | null;
  readonly disposition_decided_at: string | null;
  readonly valid_until: string | null;
  readonly valid_until_absent_reason: string | null;
  readonly shipment_units: number | null;
  readonly shipment_minimum_units: number | null;
  readonly below_shipment_minimum: boolean | null;
  readonly shipment_absent_reason: string | null;
  // The size's scale and place in its run, and who decided by name: the platform's lines file writes them from the contract that
  // follows the review of 2026-11-03; a file that does not has them `null`, and a line read as JSON (the approved lines) carries them.
  readonly size_scale?: string | null;
  readonly size_run_position?: number | null;
  readonly size_absent_reason?: string | null;
  readonly disposition_actor_name?: string | null;
  readonly disposition_actor_name_absent_reason?: 'machine' | 'service_account' | 'no_profile' | null;
  // The market's own day of the decision, or why there is none (the contract that follows the review of 2026-11-03).
  readonly decided_on_market_day?: string | null;
  readonly decided_on_market_day_absent_reason?: string | null;
}

/** `text`: a string. `nullable`: a string, `null` when empty or absent. `integer`: required. `optionalInteger`, `optionalBoolean`: `null` when empty or absent. `boolean`: required. */
type FieldKind = 'text' | 'nullable' | 'integer' | 'optionalInteger' | 'boolean' | 'optionalBoolean';

type FieldMap<T> = { readonly [K in keyof T]-?: FieldKind };

const LINE_FIELDS: FieldMap<LineRow> = {
  article_key: 'text', store_id: 'text', size_key: 'text', size_code: 'nullable', units: 'integer', ship_week: 'text', reason_code: 'text',
  expected_impact: 'text', currency_code: 'text', share: 'nullable', position_units: 'optionalInteger', core_units: 'optionalInteger',
  quantile_units: 'optionalInteger', need_units: 'optionalInteger', disposition: 'nullable', disposition_reason_code: 'nullable',
  adjusted_units: 'optionalInteger', disposition_actor: 'nullable', disposition_decided_at: 'nullable', valid_until: 'nullable',
  valid_until_absent_reason: 'nullable', shipment_units: 'optionalInteger', shipment_minimum_units: 'optionalInteger',
  below_shipment_minimum: 'optionalBoolean', shipment_absent_reason: 'nullable', size_scale: 'nullable', size_run_position: 'optionalInteger',
  size_absent_reason: 'nullable', disposition_actor_name: 'nullable', disposition_actor_name_absent_reason: 'nullable',
  decided_on_market_day: 'nullable', decided_on_market_day_absent_reason: 'nullable',
};

/** An article as the file writes it: the lists joined by commas, no sizes. */
interface ArticleFileRow extends Omit<RunArticle, 'sizes' | 'reasons' | 'notes' | 'similar_articles' | 'is_later_wave' | 'allocation_version_id'> {
  readonly reasons: string;
  readonly notes: string;
  readonly similar_articles: string | null;
}

const ARTICLE_FIELDS: FieldMap<ArticleFileRow> = {
  article_key: 'text', outcome: 'text', reasons: 'text', notes: 'text', sub_category: 'nullable', category: 'nullable', first_sale_date: 'nullable',
  stores_present: 'integer', dc_units: 'integer', committed_units: 'integer', available_units: 'integer', reserved_units: 'optionalInteger',
  allocated_units: 'optionalInteger', held_nominal_units: 'optionalInteger', held_effective_units: 'optionalInteger', chosen_scenario_id: 'nullable',
  indifferent: 'optionalBoolean', list_price: 'nullable', list_price_basis: 'nullable', landed_cost: 'nullable', cost_provenance: 'nullable',
  currency_code: 'nullable', bought_units: 'optionalInteger', store_buy_units: 'optionalInteger', similarity_basis: 'nullable',
  history_weeks: 'optionalInteger', elapsed_weeks: 'optionalInteger', similar_articles: 'nullable', undecided_lines: 'optionalInteger',
};

interface ScenarioFileRow extends Omit<RunScenario, 'identical_to'> { readonly identical_to: string }

const SCENARIO_FIELDS: FieldMap<ScenarioFileRow> = {
  scenario_id: 'text', article_key: 'text', hold_fraction: 'text', nominal_hold_units: 'integer', effective_hold_units: 'integer', push_units: 'integer',
  expected_full_price_units: 'text', expected_terminal_units: 'text', realised_margin: 'text', chosen: 'boolean', identical_to: 'text',
};

const CELL_FIELDS: FieldMap<RunCell> = {
  article_key: 'text', store_key: 'text', outcome: 'text', reason_code: 'nullable', units: 'integer', share: 'nullable', mean_units: 'nullable',
  window_mean_units: 'nullable', dispersion: 'nullable', dispersion_observed: 'optionalBoolean', position_units: 'optionalInteger',
  quantile_units: 'optionalInteger', core_units: 'optionalInteger', need_units: 'optionalInteger', horizon_units: 'optionalInteger',
  capacity_measured: 'optionalBoolean', presentation_minimum_units: 'optionalInteger', horizon_days: 'optionalInteger',
};


/** A whole text read as rows — for a caller that holds one. Pure. */
export function parseCsv(input: string): string[][] {
  return parseCsvText(input, rowReaderError);
}

function cellOf(kind: FieldKind, name: string, cell: string, line: number): string | number | boolean | null {
  switch (kind) {
    case 'text': return cell.replace(CSV_FORMULA_GUARD, '');
    case 'nullable': return cell === '' ? null : cell.replace(CSV_FORMULA_GUARD, '');
    case 'integer':
    case 'optionalInteger':
      if (cell === '' && kind === 'optionalInteger') return null;
      if (!/^-?\d+$/.test(cell)) throw new RunFileError(`line ${line}: «${name}» is not a whole number`);
      return Number(cell);
    case 'boolean':
    case 'optionalBoolean': {
      if (cell === '' && kind === 'optionalBoolean') return null;
      const word = cell.toLowerCase();
      if (word === 'true') return true;
      if (word === 'false') return false;
      throw new RunFileError(`line ${line}: «${name}» is not true or false`);
    }
    default: { const exhaustive: never = kind; throw new Error(String(exhaustive)); }
  }
}

const OPTIONAL: readonly FieldKind[] = ['nullable', 'optionalInteger', 'optionalBoolean'];

/** The reader of one line of the file, by field name from its header: (cells, line number) → the row. A required column the header lacks is a refusal. */
function rowReader<T>(header: readonly string[], fields: Readonly<Record<string, FieldKind>>): (cells: readonly string[], line: number) => T {
  const at = new Map(header.map((name, index) => [name, index]));
  const present = Object.entries(fields).map(([name, kind]): readonly [string, FieldKind, number | undefined] => {
    const index = at.get(name);
    if (index === undefined && !OPTIONAL.includes(kind)) throw new RunFileError(`the column «${name}» is missing`);
    return [name, kind, index];
  });
  return (cells, line) => {
    if (cells.length !== header.length) throw new RunFileError(`line ${line} has ${cells.length} cells, the header ${header.length}`);
    const item: Record<string, string | number | boolean | null> = {};
    for (const [name, kind, column] of present) item[name] = column === undefined ? null : cellOf(kind, name, cells[column] ?? '', line);
    return item as T;
  };
}

/** The rows of a whole text, read by field name. Pure. */
export function rowsOf<T>(text: string, fields: Readonly<Record<string, FieldKind>>): T[] {
  const [header, ...body] = parseCsv(text);
  if (header === undefined) throw new RunFileError('the file is empty');
  const rowAt = rowReader<T>(header, fields);
  return body.map((cells, index) => rowAt(cells, index + 2));
}

/**
 * The rows of a file as it streams in, one at a time — none is kept here. Progress is the rows read against the rows the platform
 * stated; when the file ends, a count other than the stated one is an `ApiIncompleteFileError` (platform ADR 6201), thrown before
 * the caller has the whole list, so a short list is never written as the whole one. A refusal while reading closes the connection.
 */
export async function* streamedRows<T>(file: CsvStream, fields: Readonly<Record<string, FieldKind>>, onProgress?: CsvProgress): AsyncGenerator<T> {
  const reader = new CsvRowReader(rowReaderError);
  let rowAt: ((cells: readonly string[], line: number) => T) | null = null;
  let count = 0;
  onProgress?.(0, file.statedRows);
  let finished = false;
  try {
    const pieces = file.pieces[Symbol.asyncIterator]();
    for (;;) {
      const next = await pieces.next();
      const completed = next.done === true ? reader.end() : reader.push(next.value);
      for (const cells of completed) {
        if (rowAt === null) { rowAt = rowReader<T>(cells, fields); continue; }
        count += 1;
        yield rowAt(cells, count + 1);
      }
      onProgress?.(file.statedRows === null ? count : Math.min(count, file.statedRows), file.statedRows);
      if (next.done === true) break;
    }
    finished = true;
  } finally {
    if (!finished) await file.cancel();
  }
  if (rowAt === null) throw new RunFileError('the file is empty');
  if (file.statedRows !== null && count !== file.statedRows) throw new ApiIncompleteFileError(file.status, count, file.statedRows);
}

const list = (joined: string): string[] => joined.split(',').filter((part) => part !== '');

const articleOf = (row: ArticleFileRow): RunArticle => ({
  ...row,
  reasons: list(row.reasons),
  notes: list(row.notes),
  similar_articles: row.similar_articles === null ? null : list(row.similar_articles),
  sizes: [],
  is_later_wave: false,
  allocation_version_id: null,
});

const scenarioOf = (row: ScenarioFileRow): RunScenario => ({ ...row, identical_to: list(row.identical_to) });

/** One list of the run: the columns it is read by, and the shape of a row once read. */
export interface ListSpec<F, T> {
  readonly fields: Readonly<Record<string, FieldKind>>;
  readonly shape: (row: F) => T;
}

export const ARTICLES_LIST: ListSpec<ArticleFileRow, RunArticle> = { fields: ARTICLE_FIELDS, shape: articleOf };
export const SCENARIOS_LIST: ListSpec<ScenarioFileRow, RunScenario> = { fields: SCENARIO_FIELDS, shape: scenarioOf };
export const LINES_LIST: ListSpec<LineRow, LineRow> = { fields: LINE_FIELDS, shape: (row) => row };
export const CELLS_LIST: ListSpec<RunCell, RunCell> = { fields: CELL_FIELDS, shape: (row) => row };

export const articlesFromCsv = (text: string): RunArticle[] => rowsOf<ArticleFileRow>(text, ARTICLE_FIELDS).map(articleOf);
export const scenariosFromCsv = (text: string): RunScenario[] => rowsOf<ScenarioFileRow>(text, SCENARIO_FIELDS).map(scenarioOf);
export const linesFromCsv = (text: string): LineRow[] => rowsOf<LineRow>(text, LINE_FIELDS);
export const cellsFromCsv = (text: string): RunCell[] => rowsOf<RunCell>(text, CELL_FIELDS);

export type ListKind = 'articles' | 'scenarios' | 'lines' | 'cells';

/** The states of decision the lines list and its file are narrowed to. */
export type LineDisposition = 'undecided' | 'approved' | 'adjusted' | 'rejected' | 'deferred';

/** The rows of a stream, each shaped as it completes; the text of the file is never held. */
export async function readListFrom<F, T>(file: CsvStream, spec: ListSpec<F, T>, onProgress?: CsvProgress): Promise<T[]> {
  const rows: T[] = [];
  for await (const row of streamedRows<F>(file, spec.fields, onProgress)) rows.push(spec.shape(row));
  return rows;
}

/**
 * One list of the run, whole, in ONE request, read as it arrives; refused when it holds fewer rows than the platform stated. The lines file
 * takes the decisions to keep (`disposition`, repeated: any of them), and its row count follows.
 */
export async function readList<F, T>(
  runId: string, kind: ListKind, spec: ListSpec<F, T>, onProgress?: CsvProgress, dispositions: readonly LineDisposition[] = [],
): Promise<T[]> {
  const file = await callOperationCsvStream(exportRun, { path: { run_id: runId }, query: { kind, ...(dispositions.length === 0 ? {} : { disposition: dispositions }) } });
  return readListFrom(file, spec, onProgress);
}
